import type { Question } from '@/types/theme.types';
import type { SectorQuestion, SectorQuestionsResponse } from './sector-questions.contract';

const MAX_PAGES = 100;
const CONCURRENCY = 4;

export function toQuestion(raw: SectorQuestion): Question {
  return {
    id: raw.id,
    themeId: raw.themeId ?? '',
    sectorId: raw.sectorId,
    textFr: raw.questionText,
    textLocal: '',
    language: raw.language ?? '',
    createdAt: raw.createdAt ?? '',
    order: raw.order,
  };
}

export async function collectAllSectorQuestions(
  fetchPage: (page: number) => Promise<SectorQuestionsResponse>,
): Promise<Question[]> {
  const first = await fetchPage(1);
  const { pages, total } = first.meta.pagination;
  if (pages > MAX_PAGES) throw new Error('QUESTION_CATALOG_TOO_LARGE');
  const responses = [first];
  for (let start = 2; start <= pages; start += CONCURRENCY) {
    const pageNumbers = Array.from(
      { length: Math.min(CONCURRENCY, pages - start + 1) },
      (_, index) => start + index,
    );
    responses.push(...await Promise.all(pageNumbers.map(fetchPage)));
  }
  const questions = new Map<string, Question>();
  for (const [index, response] of responses.entries()) {
    const pagination = response.meta.pagination;
    if (pagination.page !== index + 1 || pagination.total !== total || pagination.pages !== pages ||
        pagination.has_more !== (pagination.page < pages)) {
      throw new Error('QUESTION_CATALOG_INCOMPLETE');
    }
    for (const raw of response.data) {
      if (questions.has(raw.id)) throw new Error('QUESTION_CATALOG_DUPLICATE');
      questions.set(raw.id, toQuestion(raw));
    }
  }
  if (questions.size !== total) throw new Error('QUESTION_CATALOG_INCOMPLETE');
  return [...questions.values()];
}
